import { EnhancedDeckline } from 'types/DecklinesContext.types';

export const removeFootnoteIdsFromText = (text: string, footnote_ids: (number | null)[]) => {
  let footnotesString = '';
  let extractedText = '';
  if (footnote_ids && footnote_ids.length > 0) {
    footnotesString = footnote_ids.join(',');
  }

  if (footnotesString.length > 0) {
    // Use last index to search for footnotesString as the last portion of the text is the footnotes section
    // Eg: Deckline11 -> If footnotes array is [1], then extracted text is Deckline1 and the next 1 is the footnote superscript.
    extractedText = text.substring(0, text.lastIndexOf(footnotesString));
  }
  return extractedText;
};

/**
 * A map of numbers to their Unicode superscript equivalents
 */
const NUMBER_TO_UNICODE_MAP = {
  '0': '\u2070',
  '1': '\u00B9',
  '2': '\u00B2',
  '3': '\u00B3',
  '4': '\u2074',
  '5': '\u2075',
  '6': '\u2076',
  '7': '\u2077',
  '8': '\u2078',
  '9': '\u2079',
};

/**
 * Converts a footnote number string to its Unicode superscript equivalent
 * @param footnoteNo - The number to convert
 * @returns The Unicode superscript string
 */
export const convertFootnoteNumberToSuperscript = (footnoteNo: string) => {
  return footnoteNo
    .split('')
    .map((numPart) => NUMBER_TO_UNICODE_MAP[numPart as keyof typeof NUMBER_TO_UNICODE_MAP] || numPart)
    .join('');
};

/**
 * Unicode "modifier letter low left arrowhead" (`\u02D2`) used as the raised comma that separates
 * multiple footnote superscripts, so `[1, 2]` renders as `¹˒²`. Previously an inline literal in the
 * KPI-name export path; named here so every string-superscript consumer joins identically.
 */
const FOOTNOTE_SUPERSCRIPT_SEPARATOR = '\u02D2';

/**
 * Formats a footnote ordinal array as a single Unicode superscript string for NON-DOM contexts
 * (Excel export, AG Grid string values) — the string sibling of the DOM `FootnoteSuperscript`
 * component. Null entries are filtered; an empty/absent array yields an empty string. Multiple
 * ordinals are joined with a raised comma (e.g. `[1, 2]` → `¹˒²`).
 *
 * @param footnotes - Footnote ordinals attached to a deckline, header node, column, or cell
 * @returns the superscript string, or `''` when there are no footnotes
 */
export const formatFootnoteSuperscripts = (footnotes?: (number | null)[] | null): string =>
  (footnotes ?? [])
    .filter((footnote): footnote is number => footnote != null)
    .map((footnote) => convertFootnoteNumberToSuperscript(footnote.toString()))
    .join(FOOTNOTE_SUPERSCRIPT_SEPARATOR);

export const extractKpiNameFromDeckline = (deckline: EnhancedDeckline) => {
  const { kpi_name: kpiName, footnotes } = deckline;
  const extractedKpiName = removeFootnoteIdsFromText(kpiName!, footnotes!);
  return extractedKpiName;
};
